import _ from 'underscore';

import {assert} from 'Asserts';
import {BulkTranslation, TranslationSpec} from 'bulk-workflows/translation/BulkTranslationConstants';
import * as BulkTranslationDAO from 'bulk-workflows/translation/BulkTranslationDAO';
import BulkTranslationStore from 'bulk-workflows/translation/BulkTranslationStore';
import * as BulkTranslationUtil from 'bulk-workflows/translation/BulkTranslationUtil';
import {EventConstants} from 'constants/EventConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderDAO from 'file-browser/FolderDAO';
import FileStates from 'FileStates';
import AppDispatcher from 'lib/Dispatcher';
import * as FullScreenActions from 'nav/full-screen/FullScreenActions';
import * as ToastActions from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import notifications from 'notifications';
import {FIELD_TYPES, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import getTagSchemaFromCache from 'schemas/util/getTagSchemaFromCache';
import {SortKey} from 'search/SearchConstants';

/**
 * Convert the user's selected folders and sequences in `selectedIds` to one list of sequences with tag
 * schema `sequenceTagSchemaId` if either input parameter changes. Requires `sequenceTagSchemaId` to be
 * selected.
 *
 * NOTE: This is temporary until `MultipleFilesSelector` is fully implemented.
 */
function _fetchSequencesOnUpdate(bulkTranslationUpdates: Partial<BulkTranslation>): void {
  if (
    !_.has(bulkTranslationUpdates, 'sequenceTagSchemaId') &&
    !_.has(bulkTranslationUpdates, 'selectedIds')
  ) {
    return;
  }
  const bulkTranslation = BulkTranslationUtil.Update.update(
    BulkTranslationStore.getBulkTranslation(),
    bulkTranslationUpdates
  );
  if (!bulkTranslation.sequenceTagSchemaId) {
    return;
  }
  if (!bulkTranslation.selectedIds.length) {
    // Avoid request if nothing is selected
    AppDispatcher.dispatch({
      actionType: EventConstants.BULK_TRANSLATE_UPDATE,
      updates: {sequences: []},
    });
    return;
  }
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_TRANSLATE_UPDATE,
    updates: {sequencesLoading: true},
  });
  FolderDAO.getNestedFiles(bulkTranslation.selectedIds, {
    sort: {key: SortKey.SORT_NAME_KEY},
    tagSchema: bulkTranslation.sequenceTagSchemaId,
  })
    .done(({files}) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.BULK_TRANSLATE_UPDATE,
        updates: {sequences: files},
      });
    })
    .fail(notifications.notifyError)
    .always(() => {
      AppDispatcher.dispatch({
        actionType: EventConstants.BULK_TRANSLATE_UPDATE,
        updates: {sequencesLoading: false},
      });
    });
}

/**
 * Set translation specifications to the user's selected tag schema's translation link fields if
 * `sequenceTagSchemaId` changes. For example, if the user switches from "Plasmid" (no fields) to "Chain
 * Plasmid" ("Chain" field) tag schema, then this creates the "Chain" translation specification.
 */
function _setTranslationSpecsOnUpdate(bulkTranslationUpdates: Partial<BulkTranslation>): void {
  if (!bulkTranslationUpdates.sequenceTagSchemaId) {
    return;
  }
  const sequenceTagSchema = getTagSchemaFromCache(bulkTranslationUpdates.sequenceTagSchemaId);
  // Instead of listening to the tag schema cache separately, we assume that the user is not editing tag
  // schemas while performing bulk translation because it'd be difficult to handle all possible cases of tag
  // schema edits (new fields, deleted, etc.).
  assert(sequenceTagSchema != null, 'Sequence tag schema in bulk translation was not found in cache');
  const translationLinkFields: Array<SchemaField> = _.where(sequenceTagSchema.fields, {
    fieldType: FIELD_TYPES.TRANSLATION_LINK.type,
  });
  const translationSpecs = translationLinkFields.map((schemaField) =>
    BulkTranslationUtil.createTranslationSpec(schemaField)
  );
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_TRANSLATE_UPDATE,
    updates: {translationSpecs},
  });
}

/**
 * On any client update, the error messages from server are invalidated. Reset them.
 */
function _resetErrorsOnUpdate(): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_TRANSLATE_UPDATE,
    updates: {errors: []},
  });
}

export function update(updates: Partial<BulkTranslation>): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_TRANSLATE_UPDATE,
    updates,
  });
  _fetchSequencesOnUpdate(updates);
  _setTranslationSpecsOnUpdate(updates);
  _resetErrorsOnUpdate();
}

export function updateTranslationSpec(translationSpecIndex: number, updates: Partial<TranslationSpec>): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_TRANSLATE_UPDATE_TRANSLATION_SPEC,
    translationSpecIndex,
    updates,
  });
  _resetErrorsOnUpdate();
}

export function run(): void {
  const bulkTranslation = BulkTranslationStore.getBulkTranslation();
  assert(!bulkTranslation.sequencesLoading, 'Expected sequences in bulk translation to already be loaded');
  assert(!bulkTranslation.isRunning, 'Expected bulk translation to not already be running');
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_TRANSLATE_UPDATE,
    updates: {isRunning: true},
  });
  BulkTranslationDAO.run(bulkTranslation)
    .done(({api_identifier: proteinFolderId, errors}) => {
      if (errors) {
        // Failed
        AppDispatcher.dispatch({
          actionType: EventConstants.BULK_TRANSLATE_UPDATE,
          updates: {errors},
        });
      } else {
        // Completed
        assert(proteinFolderId, 'Expected completed bulk translation to respond with protein folder');
        ToastActions.pushToast({
          type: ToastType.INFO,
          message: 'Bulk translation completed.',
          actionText: 'Open',
          duration: 0,
          onActionClick() {
            FullScreenActions.hideScreen();
            FileBrowserActions.openFile(bulkTranslation.outputFolderId!);
          },
        });
        AppDispatcher.dispatch({
          actionType: EventConstants.BULK_TRANSLATE_RUN_COMPLETED,
          outputFolderId: bulkTranslation.outputFolderId!,
          // TODO: Remove once there's a `BulkAntibodyActions.runBulkTranslation` action
          proteinFolderId,
        });
        FileStates.get().updateSequenceVersions(bulkTranslation.sequences.map((sequence) => sequence.id));
      }
    })
    .fail(notifications.notifyError)
    .always(() => {
      AppDispatcher.dispatch({
        actionType: EventConstants.BULK_TRANSLATE_UPDATE,
        updates: {isRunning: false},
      });
    });
}

export function reset(): void {
  update(BulkTranslationUtil.createBulkTranslation());
}
